import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  CameraAlt as CameraIcon,
  PhotoLibrary as GalleryIcon,
  Description as DocumentIcon,
  Close as CloseIcon,
  Refresh as RetakeIcon,
  Check as UsePhotoIcon,
} from "@mui/icons-material";
import { compressFile } from "./imageCompressionUtil";

/**
 * Reusable File Upload Helper for Web Application
 * Features:
 * - Camera capture via HTML5 getUserMedia with browser permissions check
 * - Live camera stream + Retake step + Reliable Photo Preview (using Data URL)
 * - Gallery and Document file selection (Audio option removed)
 * - Explicit borders, generous padding, and clean spacing for all buttons
 * - Image compression and EXIF orientation handling
 * - File size validation (2MB limit) and formatting
 */

// Helper to format file size into human-readable string
export const formatFileSize = (sizeInBytes) => {
  if (!sizeInBytes || sizeInBytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
  return `${(sizeInBytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

// Helper to convert Data URL to File object
const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

const FileUploadHelper = ({
  open,
  onClose,
  from = "",
  onFileSelected,
  onError,
  maxSizeMB = 2,
}) => {
  const MAX_SIZE_BYTES = maxSizeMB * 1024 * 1024;

  // File input refs
  const galleryInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // State for Camera Modal
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStep, setCameraStep] = useState("LIVE"); // 'LIVE' | 'PREVIEW'
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Captured Photo Data URL state (for 100% reliable preview)
  const [capturedDataUrl, setCapturedDataUrl] = useState(null);

  // Stop camera stream tracks
  const stopCameraStream = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
  };

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, []);

  // Handle selected file with compression & size validation
  const processAndValidateFile = async (file, isImage = false) => {
    if (!file) return;

    try {
      setIsProcessing(true);
      let processedFile = file;

      // Compress image if applicable
      if (isImage || file.type.startsWith("image/")) {
        const compressed = await compressFile(file);
        if (compressed) {
          processedFile = new File([compressed], file.name, {
            type: compressed.type || file.type,
            lastModified: Date.now(),
          });
        }
      }

      const fileSizeInBytes = processedFile.size;
      const fileName = processedFile.name;
      const mimeType = processedFile.type;

      // 2MB size limit validation
      if (fileSizeInBytes > MAX_SIZE_BYTES) {
        const err = `File size (${formatFileSize(fileSizeInBytes)}) exceeds ${maxSizeMB}MB limit. Please select a smaller file.`;
        if (onError) onError(err);
        setIsProcessing(false);
        return;
      }

      // Trigger callback
      if (onFileSelected) {
        onFileSelected(processedFile, fileName, fileSizeInBytes, mimeType);
      }
      setIsProcessing(false);
      if (onClose) onClose();
    } catch (err) {
      console.error("Error processing file:", err);
      if (onError) onError("Error processing file.");
      setIsProcessing(false);
    }
  };

  // Open Camera using HTML5 Web APIs & Request Permissions explicitly
  const startCamera = async () => {
    setCameraError(null);
    setCameraStep("LIVE");
    setCapturedDataUrl(null);
    setIsCameraOpen(true);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera API is not supported in this browser.");
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        audio: false,
      });

      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access error:", err);
      let errMsg = "Unable to access camera.";
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errMsg = "Camera permission was denied. Please allow camera access in browser settings and try again.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        errMsg = "No camera device was found on your computer.";
      } else if (err.message) {
        errMsg = err.message;
      }
      setCameraError(errMsg);
      if (onError) onError(errMsg);
    }
  };

  const closeCameraModal = () => {
    stopCameraStream();
    setIsCameraOpen(false);
    setCameraStep("LIVE");
    setCameraError(null);
    setCapturedDataUrl(null);
  };

  // Step 1: Capture photo frame and transition to PREVIEW mode
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    const context = canvas.getContext("2d");
    if (!context) return;

    // Draw frame on canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert frame to Data URL string (ensures preview works reliably)
    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
    setCapturedDataUrl(dataUrl);

    // Stop stream & switch to PREVIEW view
    stopCameraStream();
    setCameraStep("PREVIEW");
  };

  // Retake photo: return to LIVE mode and restart camera
  const handleRetake = () => {
    setCapturedDataUrl(null);
    startCamera();
  };

  // Confirm captured photo and process file
  const handleUsePhoto = () => {
    if (!capturedDataUrl) return;

    const timeStamp = Date.now();
    const fileName = `JPEG_${timeStamp}.jpg`;
    const capturedFile = dataURLtoFile(capturedDataUrl, fileName);

    closeCameraModal();
    processAndValidateFile(capturedFile, true);
  };

  // Input change handler for file chooser
  const handleInputChange = (e, isImage = false) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      processAndValidateFile(file, isImage);
    }
    e.target.value = "";
  };

  // Option visibility logic based on `from` prop
  const showDocument = from?.toLowerCase() !== "profile";
  const showCamera = from?.toLowerCase() !== "composer";

  return (
    <>
      {/* Hidden File Inputs (Audio removed as requested) */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => handleInputChange(e, true)}
      />
      <input
        ref={documentInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/*"
        style={{ display: "none" }}
        onChange={(e) => handleInputChange(e, false)}
      />

      {/* Hidden Canvas for Frame Capture */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Option Selection Dialog */}
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 0, sm: "auto" },
            top: { xs: "auto", sm: "50%" },
            left: "50%",
            transform: { xs: "translate(-50%, 0)", sm: "translate(-50%, -50%)" },
            width: { xs: "100%", sm: 440 },
            bgcolor: "background.paper",
            borderRadius: { xs: "16px 16px 0 0", sm: "16px" },
            boxShadow: 24,
            p: 3.5,
            outline: "none",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="700" color="#111">
              Update Profile Photo
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                border: "1.5px solid #d0d0d0",
                color: "#555",
                "&:hover": { bgcolor: "#f5f5f5", borderColor: "#999" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" color="textSecondary" mb={3} lineHeight={1.5}>
            Take a new photo using your webcam or select an image file from your device.
          </Typography>

          {isProcessing ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={38} sx={{ color: "#0a66c2" }} />
              <Typography variant="body2" mt={2} color="textSecondary" fontWeight="500">
                Processing image...
              </Typography>
            </Box>
          ) : (
            <Box display="flex" justifyContent="flex-end" gap={2} mt={1}>
              {/* Use Camera Button */}
              {showCamera && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (onClose) onClose();
                    startCamera();
                  }}
                  startIcon={<CameraIcon />}
                  sx={{
                    borderRadius: "24px",
                    px: 3,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    border: "1.5px solid #0a66c2",
                    color: "#0a66c2",
                    "&:hover": {
                      bgcolor: "#f0f7ff",
                      border: "1.5px solid #084e96",
                      color: "#084e96",
                    },
                  }}
                >
                  Use Camera
                </Button>
              )}

              {/* Upload photo Button */}
              <Button
                variant="contained"
                onClick={() => {
                  if (onClose) onClose();
                  galleryInputRef.current?.click();
                }}
                startIcon={<GalleryIcon />}
                sx={{
                  borderRadius: "24px",
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: "600",
                  fontSize: "0.95rem",
                  bgcolor: "#0a66c2",
                  color: "#fff",
                  border: "1.5px solid #0a66c2",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#084e96",
                    border: "1.5px solid #084e96",
                    boxShadow: "none",
                  },
                }}
              >
                Upload photo
              </Button>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Interactive Camera & Photo Preview Modal */}
      <Modal open={isCameraOpen} onClose={closeCameraModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 560 },
            bgcolor: "#1a1a1a",
            color: "#fff",
            borderRadius: "16px",
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
            border: "1px solid #333",
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={3}
            py={2.5}
            bgcolor="#111"
            borderBottom="1px solid #2a2a2a"
          >
            <Typography variant="subtitle1" fontWeight="700" color="#fff">
              {cameraStep === "PREVIEW" ? "Photo preview" : "Take photo"}
            </Typography>
            <IconButton
              onClick={closeCameraModal}
              color="inherit"
              size="small"
              sx={{
                border: "1.5px solid #444",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)", borderColor: "#666" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Media Feed Container */}
          <Box
            position="relative"
            width="100%"
            height={380}
            bgcolor="#050505"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={1}
          >
            {cameraError ? (
              <Box p={3} textAlign="center" maxWidth={400}>
                <Typography color="#ff6b6b" variant="body2" mb={2.5} lineHeight={1.5}>
                  {cameraError}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => startCamera()}
                  sx={{
                    borderRadius: "20px",
                    px: 3,
                    py: 0.8,
                    textTransform: "none",
                    fontWeight: "600",
                    border: "1.5px solid #0a66c2",
                    color: "#0a66c2",
                    "&:hover": { bgcolor: "#f0f7ff", border: "1.5px solid #084e96" },
                  }}
                >
                  Try Again
                </Button>
              </Box>
            ) : cameraStep === "LIVE" ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            ) : (
              <img
                src={capturedDataUrl}
                alt="Captured Photo Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                }}
              />
            )}
          </Box>

          {/* Footer Control Buttons */}
          {!cameraError && (
            <Box
              px={3}
              py={2.5}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              bgcolor="#111"
              borderTop="1px solid #2a2a2a"
            >
              {cameraStep === "LIVE" ? (
                <>
                  {/* Cancel Button */}
                  <Button
                    variant="outlined"
                    onClick={closeCameraModal}
                    sx={{
                      color: "#fff",
                      border: "1.5px solid #666",
                      textTransform: "none",
                      fontWeight: "600",
                      borderRadius: "24px",
                      px: 3,
                      py: 1,
                      "&:hover": {
                        border: "1.5px solid #999",
                        bgcolor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Cancel
                  </Button>

                  {/* Take photo Button */}
                  <Button
                    variant="contained"
                    onClick={capturePhoto}
                    startIcon={<CameraIcon />}
                    sx={{
                      bgcolor: "#0a66c2",
                      color: "#fff",
                      border: "1.5px solid #0a66c2",
                      textTransform: "none",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      borderRadius: "24px",
                      px: 3.5,
                      py: 1,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#084e96",
                        border: "1.5px solid #084e96",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Take photo
                  </Button>
                </>
              ) : (
                <>
                  {/* Retake Button */}
                  <Button
                    variant="outlined"
                    onClick={handleRetake}
                    startIcon={<RetakeIcon />}
                    sx={{
                      color: "#fff",
                      border: "1.5px solid #ffffff",
                      textTransform: "none",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      borderRadius: "24px",
                      px: 3,
                      py: 1,
                      "&:hover": {
                        border: "1.5px solid #dddddd",
                        bgcolor: "rgba(255,255,255,0.12)",
                      },
                    }}
                  >
                    Retake
                  </Button>

                  {/* Use photo Button */}
                  <Button
                    variant="contained"
                    onClick={handleUsePhoto}
                    startIcon={<UsePhotoIcon />}
                    sx={{
                      bgcolor: "#0a66c2",
                      color: "#fff",
                      border: "1.5px solid #0a66c2",
                      textTransform: "none",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      borderRadius: "24px",
                      px: 3.5,
                      py: 1,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#084e96",
                        border: "1.5px solid #084e96",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Use photo
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default FileUploadHelper;
